import { buildListParams, type DataGridQuery, type PageRequest } from "@nymbl/nos2-rules";
import { useQuery } from "@tanstack/react-query";
import { auditKeys } from "@/lib/query";

export interface AuditLogEntry {
  id: string;
  actorId: string | null;
  actorEmail: string | null;
  action: string;
  entityType: string;
  entityId: string | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  metadata: Record<string, unknown> | null;
  ip: string | null;
  userAgent: string | null;
  createdAt: string;
}

interface AuditLogListResponse {
  data: AuditLogEntry[];
  nextCursor: string | null;
  prevCursor: string | null;
  hasNext: boolean;
  hasPrev: boolean;
  total: number;
}

/**
 * Admin-only audit-log list. `query.status` is reused by `useDataGrid` as the
 * generic single-value filter — here it carries the **action** filter, mapped
 * to the `action` param the endpoint expects (entityType filtering is also
 * available on the endpoint if a future view needs it).
 */
export function useAuditLog(query: DataGridQuery, pageRequest: PageRequest) {
  return useQuery({
    queryKey: [...auditKeys.list(query), pageRequest] as const,
    queryFn: async (): Promise<AuditLogListResponse> => {
      const params = buildListParams(query, pageRequest);
      // The grid's status slot is the action filter for this view.
      const action = params.get("status");
      params.delete("status");
      if (action) params.set("action", action);
      const res = await fetch(`/api/admin/audit-log?${params}`);
      if (!res.ok) throw new Error("Failed to fetch audit log");
      return res.json();
    },
  });
}
